import { Invoice } from '@/api/queries/project/useGetProjectInvoices';
import { Card } from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import { by } from '@payaca/utilities/sortable';
import { FC } from 'react';
import { InvoiceCard } from '../invoiceCard/InvoiceCard';
import { GrantPaidControls } from './GrantPaidControls';

type ProjectGrant = {
  id: string;
  name: string;
  paidAt?: string | null;
  amount: {
    value: number;
    currency: { code: string; exponent: number };
  };
};

const GrantCard: FC<{
  grant: ProjectGrant;
  grantInvoices: Invoice[];
  projectPublicId: string;
  onMarkGrantAsPaidSuccess?: () => void;
}> = ({ grant, grantInvoices, projectPublicId, onMarkGrantAsPaidSuccess }) => {
  return (
    <div className="space-y-2">
      <Card sizeVariant="xs">
        <Card.Body>
          <div className="flex items-center gap-3">
            <span className="pl-3">
              <b>{grant.name}</b> - <CurrencyValue {...grant.amount} />
            </span>
            <span className="ml-auto">
              <GrantPaidControls
                grantLineId={grant.id}
                projectPublicId={projectPublicId}
                isPaid={!!grant.paidAt}
                onSuccess={onMarkGrantAsPaidSuccess}
              />
            </span>
          </div>
          {grantInvoices
            .toSorted(by('createdAt', 'DESC'))
            .map((grantInvoice) => (
              <div key={grantInvoice.id} className="border-t mt-2">
                <InvoiceCard
                  invoice={grantInvoice}
                  onRecordPaymentSuccess={onMarkGrantAsPaidSuccess}
                  borderless
                />
              </div>
            ))}
        </Card.Body>
      </Card>
    </div>
  );
};

/**
 * Grants shown outside the payment-schedule stepper — for a Project with no
 * schedule (e.g. a flat-deposit Project) — so a grant can always be marked paid
 * regardless of whether a schedule exists to host it.
 */
export const ProjectGrantsList: FC<{
  grants: ProjectGrant[];
  invoices: Invoice[];
  projectPublicId: string;
  onMarkGrantAsPaidSuccess?: () => void;
}> = ({ grants, invoices, projectPublicId, onMarkGrantAsPaidSuccess }) => {
  if (grants.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <h3>Grants</h3>
      {grants.map((grant) => (
        <GrantCard
          key={grant.id}
          grant={grant}
          grantInvoices={invoices.filter(
            (invoice) => invoice.grantLine?.id === grant.id
          )}
          projectPublicId={projectPublicId}
          onMarkGrantAsPaidSuccess={onMarkGrantAsPaidSuccess}
        />
      ))}
    </div>
  );
};
